import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import firebase from '@modules/firebase';
import { Crop } from 'react-image-crop';
import { Card } from 'types';
import { TrackingPixel } from '.cache/__types__';

import { EmailSignatureContactDetailsFieldWidth, EmailSignatureFieldPrefixes } from './consts';

export type MenuItemDetails = {
  title: string;
  itemIcon: IconDefinition;
  index: number;
};

export enum EmailSignatureSideEditorItems {
  IMAGES = 'Images',
  DETAILS = 'Details',
  SOCIAL = 'Social',
  DESIGN = 'Design',
  BANNERS = 'Banners',
  SETTINGS = 'Settings',
  SAVE_CONTACT = 'Save Contact',
}

export enum EmailSignatureContactDetailsFieldTypes {
  PHONE = 'phone',
  EMAIL = 'email',
  ADDRESS = 'address',
  FAX = 'fax',
  WEBSITE = 'website',
  SKYPE = 'skype',
  WHATSAPP = 'whatsapp',
  SIGNAL = 'signal',
}

export enum EmailSignatureSocialLinkTypes {
  LINKEDIN = 'linkedin',
  CALENDLY = 'calendly',
  FACEBOOK = 'facebook',
  INSTAGRAM = 'instagram',
  YOUTUBE = 'youtube',
  TWITTER = 'twitter',
  TIKTOK = 'tiktok',
  SNAPCHAT = 'snapchat',
  YELP = 'yelp',
  TWITCH = 'twitch',
  DISCORD = 'discord',
  GITHUB = 'github',
  PAYPAL = 'paypal',
  VENMO = 'venmo',
  CASHAPP = 'cashapp',
}

export enum EmailSignatureFootersAndBannerTypes {
  LEGAL_DISCLAIMER = 'legal-disclaimer',
  ENVIRONMENTAL_FOOTER = 'environmental-footer',
  BANNER = 'banner',
}

type EmailSignatureFieldId = string;

export type EmailSignatureContactFieldTypeDetails = {
  title: string;
  availableIcons: EmailSignatureIconName[];
  index: number;
  type: EmailSignatureContactDetailsFieldTypes;
};

export type EmailSignaturePersonalDetailsFields = {
  firstName: string;
  lastName: string;
  jobTitle: string;
  company: string;
  headline: string;
};

export type EmailSignatureImagesTypes = 'profile-picture' | 'company-logo' | 'other';

export type EmailSignatureImageFieldId = `${EmailSignatureFieldPrefixes.IMAGE}${EmailSignatureFieldId}`;

export type EmailSignatureCrop = Crop;
export interface EmailSignatureImage {
  id: EmailSignatureImageFieldId;
  externalLink: string;
  imageUrl: string;
  shape: EmailSignatureShapes;
  size: EmailSignatureSizes;
  type: EmailSignatureImagesTypes;
  isPrimary: boolean;
  originalImageUrl: string;
  crop: EmailSignatureCrop | null;
}

export type EmailSignatureContactDetailsFieldId =
  `${EmailSignatureFieldPrefixes.CONTACT}${EmailSignatureFieldId}`;
export interface EmailSignatureContactDetailsField {
  id: EmailSignatureContactDetailsFieldId;
  type: EmailSignatureContactDetailsFieldTypes;
  label: string;
  value: string;
  baseIcon: EmailSignatureIconName;
  fieldWidth: EmailSignatureContactDetailsFieldWidth;
  iconUrl: string;
  title?: string;
  countryCode?: string;
  extension?: string;
}

export type EmailSignatureContactDetailsIndices = Array<EmailSignatureContactDetailsFieldId>;

export type EmailSignatureSocialLinkId = `${EmailSignatureFieldPrefixes.SOCIAL}${EmailSignatureFieldId}`;
export type EmailSignatureSocialLink = {
  id: EmailSignatureSocialLinkId;
  type: EmailSignatureSocialLinkTypes;
  value: string;
  iconUrl: string;
};

export type EmailSignatureLegalDisclaimerId =
  `${EmailSignatureFieldPrefixes.LEGAL_DISCLAIMER}${EmailSignatureFieldId}`;

export type EmailSignatureLegalDisclaimer = {
  id: EmailSignatureLegalDisclaimerId;
  value: string;
  fontSize: EmailSignatureSizes;
};

export type EmailSignatureEnvironmentalFooterId =
  `${EmailSignatureFieldPrefixes.ENVIRONMENTAL_FOOTER}${EmailSignatureFieldId}`;

export type EmailSignatureEnvironmentalFooter = {
  id: EmailSignatureEnvironmentalFooterId;
  baseIcon: EmailSignatureIconName;
  iconUrl: string;
  value: string;
};

export type EmailSignatureBannerId = `${EmailSignatureFieldPrefixes.BANNER}${EmailSignatureFieldId}`;

export type EmailSignatureBanner = {
  id: EmailSignatureBannerId;
  externalLink: string;
  imageUrl: string;
  size: EmailSignatureSizes;
  originalImageUrl: string;
  crop: EmailSignatureCrop | null;
};

export type EmailSignatureSaveContact = {
  cardId: string;
  buttonColorUseThemeColor: boolean;
  buttonColor?: string;
  displaySaveContact: boolean;
};

export type EmailSignatureSocialLinksIndices = Array<EmailSignatureSocialLinkId>;

export type EmailSignatureDesign = {
  font: EmailSignatureFonts;
  themeColor: string;
  contactDetailsDisplayIcon: boolean;
  contactDetailsIconUseThemeColor: boolean;
  contactDetailsIconColor?: string;
  socialLinksFillStyle: EmailSignatureFillStyle;
  socialLinksShape: EmailSignatureShapes;
  socialLinksSize: EmailSignatureSizes;
  socialLinksColorMode: EmailSignatureSocialColors;
  socialLinksColor?: string;
};

export type EmailSignatureContactDetailsFields = Record<
  EmailSignatureContactDetailsFieldId,
  EmailSignatureContactDetailsField
>;
export type EmailSignatureSocialLinks = Record<EmailSignatureSocialLinkId, EmailSignatureSocialLink>;
export type EmailSignaturesImages = EmailSignatureImage[];

export interface EmailSignatureFields {
  personalDetails: EmailSignaturePersonalDetailsFields;
  contactDetails: EmailSignatureContactDetailsFields;
  contactDetailsOrder: EmailSignatureContactDetailsIndices;
  socialLinks: EmailSignatureSocialLinks;
  socialLinksOrder: EmailSignatureSocialLinksIndices;
  design: EmailSignatureDesign;
  legalDisclaimer?: EmailSignatureLegalDisclaimer;
  environmentalFooter?: EmailSignatureEnvironmentalFooter;
  banner?: EmailSignatureBanner;
  saveContact: EmailSignatureSaveContact;
  images: EmailSignaturesImages;
}

export interface EmailSignatureMetadata {
  id: string;
  title: string;
  createdAt: firebase.firestore.Timestamp;
  updatedAt: firebase.firestore.Timestamp;
  deletedAt?: firebase.firestore.Timestamp;
  createdBy: {
    id: string;
    firstName: string;
    lastName: string;
  };
  isTeamEmailSignature: boolean;
  orgId?: string;
  userId: string;
  recipientEmail?: string;
  activationCode?: string | null;
  activatedAt?: firebase.firestore.Timestamp;
  sendActivationEmailAt?: firebase.firestore.Timestamp;
  importedCardId?: string;
  preventEditingSignature?: boolean;
  googleWorkspaceIntegrationStatus?: {
    success?: boolean;
    message?: string;
    updatedAt?: firebase.firestore.Timestamp;
    firstSyncedAt?: firebase.firestore.Timestamp;
  };
}

// here we extend the Legacy Email Signature V2 with a tracking pixel
// because we don't want to migrate all email signatures to graphQL just yet
export interface EmailSignatureV2 extends EmailSignatureFields, EmailSignatureMetadata {
  trackingPixel?: TrackingPixel;
}

export enum EmailSignatureFillStyle {
  LIGHT = 'light',
  FULL = 'full',
}

export type EmailSignatureIconName =
  | 'envelope'
  | 'envelope-open'
  | 'external-link'
  | 'envelope-open-text'
  | 'external-link'
  | 'fax'
  | 'globe'
  | 'inbox'
  | 'inbox-in'
  | 'laptop'
  | 'laptop-code'
  | 'link'
  | 'location'
  | 'location-arrow'
  | 'mailbox'
  | 'map'
  | 'map-marked-alt'
  | 'map-marker-alt'
  | 'map-pin'
  | 'mobile'
  | 'mobile-android'
  | 'paperclip'
  | 'paper-plane'
  | 'phone'
  | 'phone-alt'
  | 'phone-office'
  | 'phone-rotary'
  | 'print'
  | 'video'
  | 'skype'
  | 'snapchat'
  | 'youtube'
  | 'facebook'
  | 'linkedin'
  | 'twitter'
  | 'instagram'
  | 'twitch'
  | 'github'
  | 'calendly'
  | 'paypal'
  | 'venmo'
  | 'cashapp'
  | 'patreon'
  | 'blinq'
  | 'discord'
  | 'yelp'
  | 'signal'
  | 'tiktok'
  | 'wechat'
  | 'whatsapp'
  | 'browser'
  | 'leaf'
  | 'leaf-heart'
  | 'leaf-maple'
  | 'leaf-oak'
  | 'canadian-maple-leaf'
  | 'tree'
  | 'trees'
  | 'tree-alt'
  | 'tree-large'
  | 'tree-decorated';

export type PhoneNumberIcons = 'phone-alt' | 'phone' | 'phone-rotary' | 'phone-office' | 'mobile';
export type EmailIcons =
  | 'envelope'
  | 'envelope-open'
  | 'envelope-open-text'
  | 'inbox'
  | 'inbox-in'
  | 'mailbox';
export type WebsiteIcons = 'browser' | 'globe' | 'laptop' | 'laptop-code' | 'link' | 'external-link';
export type AddressIcons =
  | 'map-marker-alt'
  | 'map-pin'
  | 'map'
  | 'map-marked-alt'
  | 'location-arrow'
  | 'location';
export type SkypeIcons = 'skype';
export type WhatsAppIcons = 'whatsapp';
export type SignalIcons = 'signal';
export type FaxIcons = 'fax' | 'phone-office' | 'paperclip' | 'paper-plane' | 'print' | 'inbox';

export type Icons = {
  Phone: PhoneNumberIcons;
  Email: EmailIcons;
  Website: WebsiteIcons;
  Address: AddressIcons;
  Skype: SkypeIcons;
  WhatsApp: WhatsAppIcons;
  Signal: SignalIcons;
  Fax: FaxIcons;
};

export enum EmailSignatureFonts {
  Arial = 'Arial',
  Verdana = 'Verdana',
  Helvetica = 'Helvetica',
  Tahoma = 'Tahoma',
  'Trebuchet MS' = 'Trebuchet MS',
  'Times New Roman' = 'Times New Roman',
  Georgia = 'Georgia',
  Garamond = 'Garamond',
  'Courier New' = 'Courier New',
}

export enum EmailSignatureShapes {
  CIRCLE = 'circle',
  SQUARE = 'square',
  SQUIRCLE = 'squircle',
}

export enum EmailSignatureSizes {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
}

export enum EmailSignatureSocialColors {
  THEME = 'theme',
  BRAND = 'brand',
  CUSTOM = 'custom',
}

export enum SideEditorNodeKey {
  GENERAL_DETAILS_NODE = 'GENERAL_DETAILS_NODE',
  PERSONAL_DETAILS_NODE = 'PERSONAL_DETAILS_NODE',
  CONTACT_DETAILS_NODE = 'CONTACT_DETAILS_NODE',
  SOCIAL_LINKS_NODE = 'SOCIAL_LINKS_NODE',
  NEW_CONTACT_DETAILS_NODE = 'NEW_CONTACT_DETAILS_NODE',
  EMAIL_SIGNATURE_THEME_NODE = 'EMAIL_SIGNATURE_THEME_NODE',
  CONTACT_DETAILS_DESIGN_NODE = 'CONTACT_DETAILS_DESIGN_NODE',
  SOCIAL_LINKS_DESIGN_NODE = 'SOCIAL_LINKS_DESIGN_NODE',
  SAVE_CONTACT_DESIGN_NODE = 'SAVE_CONTACT_DESIGN_NODE',
  SEPARATOR_DESIGN_NODE = 'SEPARATOR_DESIGN_NODE',
  BANNER_BOX_NODE = 'BANNER_BOX_NODE',
  ENVIRONMENTAL_FOOTER_BOX_NODE = 'ENVIRONMENTAL_FOOTER_BOX_NODE',
  LEGAL_DISCLAIMER_BOX_NODE = 'LEGAL_DISCLAIMER_BOX_NODE',
}

export enum EmailSignatureFooters {
  DISCLAIMER = 'Disclaimer',
  ENVIRONMENT_FOOTER = 'Environment footer',
  CUSTOM_BANNER = 'Custom banner',
}

export type EmailSignatureFooterDetails = {
  title: EmailSignatureFooters;
  icon: IconDefinition;
};
export interface EmailSignatureCardOptionToImportOrLink extends Card {
  label: string;
  title: string;
  value: string;
}

export type EmailSignatureGroupedCardsOptions = {
  label: string;
  options: EmailSignatureCardOptionToImportOrLink[];
};

export enum EmailSignatureTabNames {
  TEAM = 'team',
  PERSONAL = 'personal',
}

export type EmailSignatureRowData = EmailSignatureV2;

export enum EmailSignatureClients {
  GMAIL = 'Gmail',
  //APPLE = "Apple Mail",
  OUTLOOK = 'Outlook',
  //YAHOO = "Yahoo",
  //MACMAIL = "MacMail",
  IOS_MAIL_DESKTOP = 'Mail-Desktop',
  IOS_MAIL_IPHONE = 'Mail-iPhone',
}

export type EmailSignatureSocialLinkDesign = {
  socialLinksFillStyle: EmailSignatureFillStyle;
  socialLinksShape: EmailSignatureShapes;
  socialLinksSize: EmailSignatureSizes;
  socialLinksColorMode: EmailSignatureSocialColors;
  socialLinksColor?: string;
};

export type EmailSignatureContactDetailsFieldsDesign = {
  contactDetailsIconUseThemeColor: boolean;
  contactDetailsIconColor?: string;
};

export type EmailSignatureFieldIdTypes =
  | EmailSignatureSocialLinkId
  | EmailSignatureContactDetailsFieldId
  | EmailSignatureBannerId
  | EmailSignatureEnvironmentalFooterId
  | EmailSignatureLegalDisclaimerId
  | EmailSignatureImageFieldId;

export type EmailSignatureBasicSelectOption = {
  label: string;
  value: string;
};

export interface SelectEmailSignatureOption extends EmailSignatureV2 {
  label: string;
  title: string;
  value: string;
}

export type SelectEmailSignatureGroupOption = {
  label: string;
  options: Array<SelectEmailSignatureOption>;
};

export type TeamEmailSignatureBulkCreateRequest = {
  id: string;
  emailSignatureId: string | null;
  requestedAt: firebase.firestore.Timestamp;
  timezone: string;
  userId: string;
  orgId: string;
  instructionsSentAt: firebase.firestore.Timestamp | null;
  blinqNotificationSentAt: firebase.firestore.Timestamp | null;
};

export interface GeneratedCardForEmailSignature extends Card {
  emailSignatureId: string;
  isInvisible: true;
}

export interface EmailSignatureProxyImage {
  imageId: string;
  url: string;
}
